import { useRBACPermissions } from '@/contexts/useRBACPermissions'

export type LicensePermissions = {
  /**
   * Whether the viewer may see or create licenses.
   *
   * Reads `false` while `isResolved` is `false` — the viewer's roles simply
   * aren't known yet. Treat it as an answer only once `isResolved` is `true`.
   */
  canManage: boolean
  /**
   * Whether the viewer holds the `admin` role specifically.
   *
   * Distinct from `canManage`, which gtm also satisfies. Regenerating a
   * connection string is the one license action gtm cannot reach, because it
   * disconnects the customer's brokers (APP-2379).
   *
   * Reads `false` while `isResolved` is `false`, same caveat as `canManage`.
   * Hiding an affordance on that false negative self-corrects when the roles
   * arrive, so callers that only hide UI need no `isResolved` gate.
   */
  isAdmin: boolean
  /**
   * Whether the roles query has settled (succeeded or failed).
   *
   * The roles query starts out pending with no data, which makes every rights
   * check read `false`. Callers that only hide UI can render on `canManage`
   * alone — they self-correct once the roles arrive. Callers that take an
   * irreversible action on a denial (redirecting away, for example) MUST wait
   * for `isResolved`, or a deep link is bounced before the viewer's roles have
   * even been fetched.
   *
   * A failed roles query counts as resolved: we can't establish rights, so the
   * viewer is denied rather than left on a skeleton forever.
   */
  isResolved: boolean
}

/**
 * Single source of truth for "this viewer may see or create licenses".
 *
 * Evaluated in every place the license surface is rendered
 * (OrgDisambiguationCard license count, LicensesCard, CreateLicenseDialog
 * button, LicenseDetailPage). Centralizing the capability check keeps them in
 * sync when the definition evolves (e.g. M2 may split view vs create).
 *
 * Truth table (once resolved):
 *   admin=true  gtm=*      → true
 *   admin=false gtm=true   → true
 *   admin=false gtm=false  → false
 */
export function useCanManageLicenses(): LicensePermissions {
  const { hasAdminRights, hasGtmRights, userRoles } = useRBACPermissions()

  return {
    canManage: hasAdminRights() || hasGtmRights(),
    isAdmin: hasAdminRights(),
    isResolved: userRoles.isSuccess || userRoles.isError,
  }
}
